/**
 * Cartão da TRAVA DE CONFIRMAÇÃO DE ESCRITAS (no chat).
 *
 * Renderizado a partir de um `MessagePart` `type: 'confirmation'` na mensagem do
 * assistant. Enquanto `pending`, oferece Confirmar/Cancelar; a escrita só executa
 * ao confirmar (`executeAgentTool` com `confirmed: true`). O desfecho é persistido
 * no próprio part (Firestore), então recarregar a página reflete o estado final
 * (confirmed/cancelled não mostram mais botões).
 */
import type { AgentToolName, MessagePart } from '@agente/shared'
import { Ban, CheckCircle2, ShieldAlert } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Button } from '@/components/ui/button'
import { useAuth } from '@/lib/auth'
import { executeAgentTool } from '@/lib/callables'
import {
  clearWhatsappPendingConfirmationIfMatches,
  updateMessagePartConfirmation,
} from '@/lib/firestore'

export interface ConfirmationCardProps {
  /** O part de confirmação a renderizar. */
  part: MessagePart
  /** Conversa que contém a mensagem (nunca null quando há mensagens renderizadas). */
  conversationId: string | null
  /** Id da mensagem do assistant que carrega este part. */
  messageId: string
  /** Índice do part no array `parts` da mensagem (necessário para a escrita). */
  partIndex: number
}

/** Ação em andamento — protege contra duplo clique enquanto uma decisão é aplicada. */
type Busy = 'confirm' | 'cancel' | null

export function ConfirmationCard({
  part,
  conversationId,
  messageId,
  partIndex,
}: ConfirmationCardProps) {
  const { user } = useAuth()
  const uid = user?.uid ?? null

  const [busy, setBusy] = useState<Busy>(null)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  // Cache-hit do guard de idempotência: a ação JÁ havia sido realizada por uma
  // confirmação anterior — nada foi executado agora. Nunca apresentar como um
  // sucesso novo. Vale só para a sessão viva (após recarregar, é 'confirmed').
  const [deduped, setDeduped] = useState(false)
  // Trava local: uma vez resolvido, prevalece sobre o snapshot. Evita reoferecer
  // "Confirmar" (e, portanto, reexecutar a escrita) caso a gravação do status
  // falhe DEPOIS de a ação já ter sido executada no backend.
  const [localStatus, setLocalStatus] = useState<MessagePart['confirmationStatus'] | null>(null)

  const status = localStatus ?? part.confirmationStatus
  const summary = part.confirmationSummary ?? 'Confirmar esta ação?'

  async function handleConfirm() {
    if (busy) return
    if (!uid || !conversationId || !part.toolName) {
      setErrorMsg('Não foi possível confirmar agora. Recarregue a página e tente novamente.')
      return
    }
    setBusy('confirm')
    setErrorMsg(null)
    try {
      const { data } = await executeAgentTool({
        name: part.toolName as AgentToolName,
        args: part.toolArgs ?? {},
        confirmed: true,
        // Chave de idempotência estável (sobrevive a recarregar a página +
        // reconfirmar): identifica esta confirmação de forma única, então o
        // backend executa a ação no máximo uma vez.
        idempotencyKey: `${conversationId}:${messageId}:${partIndex}`,
        // Necessário para tools que postam o resultado na conversa (startEmailResearch).
        ...(conversationId ? { conversationId } : {}),
        // Insumo de origem para createReminder (sourceContext.summary): a mensagem
        // do usuário que originou a ação, capturada quando o cartão foi criado.
        ...(part.confirmationUserText ? { userText: part.confirmationUserText } : {}),
      })
      const result = data.result
      if ('needsConfirmation' in result && result.needsConfirmation === true) {
        // Inesperado com confirmed:true — mantém pendente para nova tentativa.
        setErrorMsg('A confirmação não foi aceita. Tente novamente.')
        toast.error('Não foi possível concluir a ação')
        return
      }
      if (result.ok) {
        // A escrita foi executada (ou já havia sido, se deduped). Trava localmente
        // ANTES de persistir para nunca reoferecer "Confirmar", mesmo que a
        // gravação do status falhe.
        const wasDeduped = data.deduped === true
        setLocalStatus('confirmed')
        if (wasDeduped) setDeduped(true)
        try {
          await updateMessagePartConfirmation(
            uid,
            conversationId,
            messageId,
            partIndex,
            'confirmed',
          )
        } catch (persistErr) {
          console.error('Falha ao salvar o status da confirmação:', persistErr)
          toast.warning('Ação concluída, mas não foi possível salvar o status.')
        }
        if (wasDeduped) {
          // Honestidade: um cache-hit nunca é apresentado como sucesso novo.
          toast.info('Esta ação já havia sido realizada.')
        } else {
          toast.success('Ação concluída.')
        }
      } else {
        // Erro de execução (sem needsConfirmation): mantém pendente e permite retry.
        setErrorMsg(result.error || 'Não foi possível concluir a ação.')
        toast.error('Não foi possível concluir a ação', { description: result.error })
      }
    } catch (err) {
      console.error('Falha ao executar a ação confirmada:', err)
      setErrorMsg('Falha ao concluir a ação. Tente novamente.')
      toast.error('Falha ao concluir a ação')
    } finally {
      setBusy(null)
    }
  }

  async function handleCancel() {
    if (busy) return
    if (!uid || !conversationId) {
      setErrorMsg('Não foi possível cancelar agora. Recarregue a página e tente novamente.')
      return
    }
    setBusy('cancel')
    setErrorMsg(null)
    try {
      await updateMessagePartConfirmation(uid, conversationId, messageId, partIndex, 'cancelled')
      setLocalStatus('cancelled')
      // Best-effort: se essa mesma confirmação também estiver pendente no
      // WhatsApp, limpa lá também — nunca bloqueia o cancelamento no app.
      try {
        await clearWhatsappPendingConfirmationIfMatches(uid, conversationId, messageId, partIndex)
      } catch (whatsappErr) {
        console.error('Falha ao limpar confirmação pendente do WhatsApp:', whatsappErr)
      }
    } catch (err) {
      console.error('Falha ao cancelar a ação:', err)
      setErrorMsg('Não foi possível cancelar. Tente novamente.')
      toast.error('Não foi possível cancelar')
    } finally {
      setBusy(null)
    }
  }

  if (status === 'confirmed') {
    return (
      <div className="border-border bg-muted/30 rounded-xl border px-3.5 py-3">
        <div className="flex items-start gap-2.5">
          <CheckCircle2 className="text-primary mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-foreground/80 text-sm">{summary}</p>
            <p className="text-primary text-xs font-medium">
              {deduped ? 'Esta ação já havia sido realizada.' : 'Ação concluída'}
            </p>
          </div>
        </div>
      </div>
    )
  }

  if (status === 'cancelled') {
    return (
      <div className="border-border bg-muted/30 rounded-xl border px-3.5 py-3">
        <div className="flex items-start gap-2.5">
          <Ban className="text-muted-foreground mt-0.5 size-4 shrink-0" />
          <div className="min-w-0 flex-1 space-y-0.5">
            <p className="text-muted-foreground text-sm">{summary}</p>
            <p className="text-muted-foreground text-xs font-medium">Ação cancelada</p>
          </div>
        </div>
      </div>
    )
  }

  // Só o estado 'pending' é acionável. Qualquer outro (incl. ausente) apenas
  // exibe o resumo, sem botões — nunca dispara uma escrita sem clique explícito.
  if (status !== 'pending') {
    return (
      <div className="border-border bg-muted/30 rounded-xl border px-3.5 py-3">
        <p className="text-foreground/80 text-sm">{summary}</p>
      </div>
    )
  }

  return (
    <div className="border-primary/30 bg-primary/[0.04] rounded-xl border px-3.5 py-3">
      <div className="flex items-start gap-2.5">
        <ShieldAlert className="text-primary mt-0.5 size-4 shrink-0" />
        <div className="min-w-0 flex-1 space-y-2.5">
          <div className="space-y-0.5">
            <p className="text-foreground text-xs font-semibold tracking-wide uppercase">
              Confirmação necessária
            </p>
            <p className="text-foreground/90 text-sm">{summary}</p>
          </div>
          {errorMsg ? <p className="text-destructive text-xs">{errorMsg}</p> : null}
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void handleConfirm()} disabled={busy !== null}>
              {busy === 'confirm' ? 'Executando…' : 'Confirmar'}
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => void handleCancel()}
              disabled={busy !== null}
            >
              Cancelar
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
